import { Component, Directive, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, inject, output } from "@angular/core";
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormControlDirective,
    FormControlName,
    FormGroup,
    FormGroupDirective,
    NgControl,
    NgModel,
    UntypedFormBuilder,
    ValidationErrors,
    Validator
} from "@angular/forms";
import { Subscription, tap } from "rxjs";

/**
 * Basic implementation of the ControlValueAccessor members
 * It usually should be enough by only implementing the writeValue method but
 * descendants can override others as needed. In order for this to be used
 * as a control value accessor remember to provide NG_VALUE_ACCESSOR at component
 * level, e.g.:
 * {
        provide: NG_VALUE_ACCESSOR,
        useExisting: MyComponent,
        multi: true
    }
*/
@Directive()
export abstract class ControlBase<TEntity> implements ControlValueAccessor, Validator, OnInit {
    private _isDisabled = false;
    private _injector = inject(Injector);
    private _ngControl: FormControl<TEntity>;

    readonly onBlur = output();

    ngOnInit(): void {
        this.setupNgControl();
    }

    onChange(obj: TEntity) {}

    onTouched() {}

    abstract writeValue(obj: TEntity): void;

    registerOnChange(fn: (obj: TEntity) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    @Input() get disabled() {
        return this._isDisabled;
    }

    set disabled(value: boolean) {
        this._isDisabled = value;
    }

    /**
     * Validator members
     *
     * By default this function will inform that no error exists - should be implemented
     * in descendants with desired logic if it applies. In order for this to be used with
     * the Forms API remember to provide NG_VALIDATORS at component level, e.g.:
     *
     * {
            provide: NG_VALIDATORS,
            useExisting: MyComponent,
            multi: true
        }
     */
    validate(control: AbstractControl): ValidationErrors | null {
        return null;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
    }

    protected get ngControl() {
        return this._ngControl;
    }

    protected _onBlur() {
        this.onTouched();
        this.onBlur.emit();
    }

    private setupNgControl() {
        const injectControl = this._injector?.get(NgControl, null, { optional: true });
        if (!injectControl) {
            return;
        }

        switch (injectControl.constructor) {
            case NgModel:
                this._ngControl = (injectControl as NgModel).control;
                break;
            case FormControlName:
                this._ngControl = this._injector.get(FormGroupDirective).getControl(injectControl as FormControlName);
                break;
            default:
                this._ngControl = (injectControl as FormControlDirective).form;
                break;
        }
    }
}

/**
 * @deprecated
 * Do not use this base class for new custom controls.
 * Migrate existing consumers as they are updated; remove this class once no consumers remain.
 */
@Component({ template: "" })
export abstract class ControlWithFormGroupBaseComponent<TEntity> extends ControlBase<TEntity> implements OnDestroy {
    private _form: FormGroup = this.formBuilder.group({});
    protected onChangeSubscription: Subscription;

    get form() {
        return this._form;
    }

    constructor(protected formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnDestroy(): void {
        this.onChangeSubscription?.unsubscribe();
    }

    formChanged(value: TEntity): void {}

    /**
     * Disable supplied controls without emitting events which means we don't get
     * Maximum Call Stack Size Exceeded. If you call disable on the control itself
     * instead of this method make sure to pass the { emitEvent: false }
     * @param controls Controls to disable
     */
    disableControls(...controls: AbstractControl[]) {
        controls.forEach(c => c.disable({ emitEvent: false }));
    }

    /**
     * Enable supplied controls without emitting events which means we don't get
     * Maximum Call Stack Size Exceeded. If you call enable on the control itself
     * instead of this method make sure to pass the { emitEvent: false }
     * @param controls Controls to enable
     */
    enableControls(...controls: AbstractControl[]) {
        controls.forEach(c => c.enable({ emitEvent: false }));
    }

    writeValue(obj: TEntity): void {
        this.form.patchValue(obj, { emitEvent: false });
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable({ emitEvent: false });
        } else {
            this.form.enable({ emitEvent: false });
        }
    }

    /**
     * Setups the error validation key to be used. By default it's invalid
     *
     * The ValidationErrors instance will look something like:
     * {
     *   invalid: {
     *     error1: ...
     *     error2: ...
     *     ...
     *   }
     * }
     */
    get errorKey(): string {
        return "invalid";
    }

    validate(_: AbstractControl): ValidationErrors | null {
        if (this.form.invalid) {
            const validationErrors: ValidationErrors = {};
            validationErrors[this.errorKey] = this.form.errors;
            return validationErrors;
        }
        return null;
    }

    protected setForm(form: FormGroup) {
        this._form = form;
        this.onChangeSubscription?.unsubscribe();
        this.onChangeSubscription = this._form.valueChanges
            .pipe(
                tap((value: TEntity) => {
                    try {
                        this.formChanged(value);
                        this._form.updateValueAndValidity({ emitEvent: false });
                    } catch {}
                })
            )
            .subscribe((value: TEntity) => this.onChange(value));
    }
}
